import type { QualitySample } from "../api/parse.ts";

type QualitySampleView = QualitySample;

const WIDTH = 720;
const HEIGHT = 150;
const PAD_LEFT = 52;
const PAD_RIGHT = 16;
const PAD_TOP = 18;
const PAD_BOTTOM = 36;
const PLOT_W = WIDTH - PAD_LEFT - PAD_RIGHT;
const PLOT_H = HEIGHT - PAD_TOP - PAD_BOTTOM;
const WINDOW_S = 120;

type Read = (sample: QualitySampleView) => number | null;

type SeriesSpec = {
  title: string;
  read: Read;
  scale: (values: number[]) => { unit: string; top: number; plot: (value: number) => number };
};

export function QualityCharts(props: { samples: QualitySampleView[] }) {
  const newest = newestMs(props.samples);
  return (
    <div className="quality-charts">
      {SPECS.map((spec) => (
        <SeriesChart key={spec.title} spec={spec} samples={props.samples} newest={newest} />
      ))}
    </div>
  );
}

function SeriesChart(props: { spec: SeriesSpec; samples: QualitySampleView[]; newest: number | null }) {
  const points = props.samples.map((sample) => ({
    seconds: secondsBefore(sample, props.newest),
    value: props.spec.read(sample),
  }));
  const numbers = points.flatMap((point) => (point.value === null ? [] : [point.value]));
  if (numbers.length === 0) {
    return <p>{`${props.spec.title}, Not measured`}</p>;
  }
  const scaled = props.spec.scale(numbers);
  const plotted = points.flatMap((point) =>
    point.value === null || point.seconds === null ? [] : [{ seconds: point.seconds, y: scaled.plot(point.value) }],
  );
  const latest = plotted.length > 0 ? plotted[plotted.length - 1]?.y ?? null : null;
  const range = plotted.reduce(
    (span, point) => ({ min: Math.min(span.min, point.y), max: Math.max(span.max, point.y) }),
    { min: plotted[0]?.y ?? 0, max: plotted[0]?.y ?? 0 },
  );
  const yTicks = axisTicks(scaled.top);
  const label = `${props.spec.title}, ${scaled.unit}, last 120 s, latest ${compact(latest ?? 0)}, range ${compact(range.min)} to ${compact(range.max)}`;
  const line = plotted.map((point) => `${xOf(point.seconds).toFixed(1)},${yOf(point.y, scaled.top).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width="100%" role="img" aria-label={label}>
      <rect x="0" y="0" width={WIDTH} height={HEIGHT} fill="#fffaf3" />
      {yTicks.map((tick) => (
        <line
          key={`g-${tick}`}
          x1={PAD_LEFT}
          x2={WIDTH - PAD_RIGHT}
          y1={yOf(tick, scaled.top)}
          y2={yOf(tick, scaled.top)}
          stroke="#e4ddd2"
          strokeWidth="1"
        />
      ))}
      <polyline fill="none" stroke="#1f4e46" strokeWidth="2" points={line} />
      {plotted.map((point, index) => (
        <circle key={`p-${index}`} cx={xOf(point.seconds)} cy={yOf(point.y, scaled.top)} r="3" fill="#1f4e46" />
      ))}
      <text className="axis-title" x={PAD_LEFT} y="14" fill="#241f1a" fontSize="12" aria-hidden="true">
        {props.spec.title} ({scaled.unit})
      </text>
      {yTicks.map((tick) => (
        <text
          key={`y-${tick}`}
          className="y-tick"
          x="4"
          y={yOf(tick, scaled.top) + 4}
          fill="#241f1a"
          fontSize="12"
          aria-hidden="true"
        >
          {tick === scaled.top ? `${compact(tick)} ${scaled.unit}` : compact(tick)}
        </text>
      ))}
      {X_TICKS.map((tick) => (
        <text
          key={`x-${tick}`}
          className="x-tick"
          x={xOf(tick)}
          y={HEIGHT - 16}
          fill="#241f1a"
          fontSize="12"
          textAnchor="middle"
          aria-hidden="true"
        >
          {tick === 0 ? "now" : `${tick} s`}
        </text>
      ))}
      <text className="axis-title" x={WIDTH / 2} y={HEIGHT - 2} fill="#241f1a" fontSize="12" textAnchor="middle" aria-hidden="true">
        Time (s)
      </text>
    </svg>
  );
}

const X_TICKS = [-120, -90, -60, -30, 0];

const SPECS: SeriesSpec[] = [
  { title: "Bitrate", read: (sample) => sample.bitrate, scale: bitrateScale },
  { title: "Frame rate", read: (sample) => sample.fps, scale: (values) => unitScale("fps", Math.max(12, niceCeil(Math.max(...values)))) },
  { title: "Round trip", read: (sample) => sample.rttMs, scale: (values) => unitScale("ms", Math.max(100, niceCeil(Math.max(...values)))) },
  { title: "Jitter", read: (sample) => sample.jitterMs, scale: (values) => unitScale("ms", Math.max(100, niceCeil(Math.max(...values)))) },
  {
    title: "Loss",
    read: (sample) => (sample.packetLoss === null ? null : sample.packetLoss * 100),
    scale: (values) => unitScale("%", Math.max(1, niceCeil(Math.max(...values)))),
  },
];

function bitrateScale(values: number[]): { unit: string; top: number; plot: (value: number) => number } {
  const maxBit = Math.max(...values);
  const mbit = maxBit >= 10_000_000;
  const unit = mbit ? "Mbit/s" : "kbit/s";
  const plot = (value: number) => (mbit ? value / 1_000_000 : value / 1000);
  const top = Math.max(1, niceCeil(Math.max(...values.map(plot))));
  return { unit, top, plot };
}

function unitScale(unit: string, top: number): { unit: string; top: number; plot: (value: number) => number } {
  return { unit, top, plot: (value) => value };
}

function newestMs(samples: QualitySampleView[]): number | null {
  let newest: number | null = null;
  for (const sample of samples) {
    const at = Date.parse(sample.sampledAt);
    if (Number.isNaN(at)) continue;
    if (newest === null || at > newest) newest = at;
  }
  return newest;
}

function secondsBefore(sample: QualitySampleView, newest: number | null): number | null {
  if (newest === null) return null;
  const at = Date.parse(sample.sampledAt);
  if (Number.isNaN(at)) return null;
  return (at - newest) / 1000;
}

function xOf(seconds: number): number {
  const clamped = Math.min(0, Math.max(-WINDOW_S, seconds));
  return PAD_LEFT + ((clamped + WINDOW_S) / WINDOW_S) * PLOT_W;
}

function yOf(value: number, top: number): number {
  const span = top > 0 ? top : 1;
  const ratio = Math.min(1, Math.max(0, value / span));
  return PAD_TOP + (1 - ratio) * PLOT_H;
}

function axisTicks(top: number): number[] {
  for (const divisions of [4, 3, 2]) {
    const step = top / divisions;
    const ticks = Array.from({ length: divisions + 1 }, (_, index) => step * index);
    if (ticks.length >= 3 && ticks.length <= 5 && ticks.every((tick) => nearlyNice(tick))) return ticks;
  }
  return [0, top / 2, top];
}

function nearlyNice(value: number): boolean {
  if (value === 0) return true;
  const abs = Math.abs(value);
  const exp = Math.floor(Math.log10(abs));
  const base = 10 ** exp;
  const n = abs / base;
  return [1, 2, 2.5, 5, 10].some((step) => Math.abs(n - step) < 1e-6);
}

function niceCeil(value: number): number {
  if (!(value > 0)) return 1;
  const exp = Math.floor(Math.log10(value));
  const base = 10 ** exp;
  const n = value / base;
  const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return nice * base;
}

function compact(value: number): string {
  if (Number.isInteger(value)) return String(value);
  const two = Math.round(value * 100) / 100;
  if (Number.isInteger(two)) return String(two);
  const one = Math.round(value * 10) / 10;
  if (Math.abs(value - one) < 1e-9) return one.toFixed(1);
  return two.toFixed(2);
}
